import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { RootState, setCurrentUserCampus, setCurrentUserCampusId } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import SchedulesService from "../../../../services/campus/schedules.service";
import useEsTelefono from "../../../../utils/esTelefono";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { getAccessPermissions } from "../../../../common";
import { buildPages } from "../enrollmentRequests/helpers";
import HorarioForm from "./horarioForm";
import VentanaHorario from "./ventanaHorario";
import ActualizarMatricula from "./actualizarMatricula";
import HorariosView from "./horariosView";
import { tituloHorario } from "./formato";
import { FichaHorario, FilaHorario, Filtros, FILTROS_VACIOS, FormularioPedido, HorariosLista, OpcionesHorarios, TabKey } from "./interfaces";
import S from "./strings";
import "./horarios.css";

/*
	Horarios (Horarios Claros, 2026-11-01): una lista con pestañas (En Curso, Por Empezar, Terminados, Todos y Revisar), buscador y
	pastillas de filtro; el clic en una fila abre la ventana del horario. Ocupación de Aulas y Nuevo Horario salen de aquí.
	Enlaces que siguen sirviendo: ?horario=<código>&plantel=<id> (abre la ventana), &actualizar=1 (abre Actualizar Matrícula) y
	?nuevo=1&nivel=<id> (abre el formulario con ese módulo).
*/
const http = HttpManager.getInstance();
const PAGE_SIZES = [50, 100];

type Origen = "lista" | "ventana" | "ocupacion";

const HorariosController: React.FC = () => {
	const movil = useEsTelefono(760);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [params, setParams] = useSearchParams();
	const perfil: any = useSelector((state: RootState) => (state as any).userProfile?.value);
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const campusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const campusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = campusKey && campusRef ? campusRef[campusKey] : null;
	const campusId: number | null = legacyCampus?.id ?? sessionCampus?.id ?? null;
	const sym: string = sessionCampus?.currency || legacyCampus?.currency || "";
	const allowed: any = getAccessPermissions(perfil, "schedules");
	const matriculas: any = getAccessPermissions(perfil, "enrollments");
	const asistencias: any = getAccessPermissions(perfil, "attendances");

	const [tab, setTab] = useState<TabKey>("enCurso");
	// Desde Cursos › Ver Horarios llega /schedules?curso=<id> (o ?q=<texto>): la lista abre ya filtrada.
	const [q, setQ] = useState<string>(() => params.get("q") || "");
	const [filtros, setFiltros] = useState<Filtros>(() => Number(params.get("curso")) > 0 ? { ...FILTROS_VACIOS, cursos: [Number(params.get("curso"))] } : FILTROS_VACIOS);
	const [page, setPage] = useState<number>(1);
	const [perPage, setPerPage] = useState<number>(PAGE_SIZES[0]);
	const [datos, setDatos] = useState<HorariosLista | null>(null);
	const [counts, setCounts] = useState<HorariosLista["counts"] | null>(null);
	const [opciones, setOpciones] = useState<OpcionesHorarios | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	const [refresco, setRefresco] = useState<number>(0);
	const [ventanaId, setVentanaId] = useState<number | null>(null);
	const [form, setForm] = useState<{ pedido: FormularioPedido; origen: Origen } | null>(null);
	const [matricula, setMatricula] = useState<FilaHorario | null>(null);
	const [borrar, setBorrar] = useState<FilaHorario | null>(null);
	const [borrando, setBorrando] = useState<boolean>(false);
	const [errorBorrar, setErrorBorrar] = useState<string | null>(null);
	const pedido = useRef(0);
	const atendido = useRef<string | null>(null);
	const campusPrevio = useRef<number | null>(campusId);
	const refrescar = () => setRefresco((x) => x + 1);

	// Al cambiar de plantel todo empieza de cero.
	useEffect(() => {
		if (campusPrevio.current === campusId) return;
		campusPrevio.current = campusId;
		setDatos(null); setCounts(null); setOpciones(null); setPage(1); setFiltros(FILTROS_VACIOS); setQ(""); setTab("enCurso");
		setVentanaId(null); setForm(null); setAviso(null);
	}, [campusId]);

	// Opciones de las pastillas (una vez por plantel).
	useEffect(() => {
		if (!campusId) return;
		let vigente = true;
		http.horariosService.opciones(campusId).then((r) => { if (vigente) setOpciones(r.data); }).catch((e) => console.error(e));
		return () => { vigente = false; };
	}, [campusId]);

	// La lista. Solo vale la respuesta de la última petición.
	useEffect(() => {
		if (!campusId) return;
		const n = ++pedido.current;
		setLoading(true);
		setError(null);
		http.horariosService.lista(campusId, {
			tab, q, page, perPage, kitPendiente: filtros.kitPendiente,
			courseIds: filtros.cursos, levelIds: filtros.modulos, mentorIds: filtros.maestras, classroomIds: filtros.aulas, weekDays: filtros.dias, periodIds: filtros.periodos
		}).then((r) => {
			if (n !== pedido.current) return;
			setDatos(r.data); setCounts(r.data.counts); setLoading(false);
		}).catch((e) => {
			console.error(e);
			if (n !== pedido.current) return;
			setError(S.errorLista); setLoading(false);
		});
	}, [campusId, tab, q, page, perPage, filtros, refresco]);

	// ---- ventana y enlace
	const quitarEnlace = () => {
		const x = new URLSearchParams(params);
		if (x.has("horario") || x.has("plantel") || x.has("actualizar")) {
			x.delete("horario"); x.delete("plantel"); x.delete("actualizar");
			setParams(x, { replace: true });
		}
	};
	const abrirVentana = (id: number, code?: string) => {
		if (code && campusId) {
			const x = new URLSearchParams(params);
			atendido.current = "h:" + code;
			x.set("horario", code); x.set("plantel", String(campusId));
			setParams(x, { replace: true });
		}
		setVentanaId(id);
	};
	const cerrarVentana = () => { setVentanaId(null); quitarEnlace(); };

	// Enlaces: /schedules?horario=<código>&plantel=<id>[&actualizar=1] y /schedules?nuevo=1&nivel=<id>
	useEffect(() => {
		if (!campusRef || !campusKey) return;
		const codigo = params.get("horario");
		const nuevo = params.get("nuevo");
		const llave = codigo ? "h:" + codigo : nuevo ? "n:" + (params.get("nivel") || "") : null;
		if (!llave) { atendido.current = null; return; }
		if (atendido.current === llave) return;
		const plantel = Number(params.get("plantel") || 0);
		if (plantel > 0 && campusRef[campusKey]?.id !== plantel) {
			if (campusRef["C_" + plantel] == null) {
				atendido.current = llave;
				setAviso("El enlace es de un plantel al que este usuario no tiene acceso.");
				setParams(new URLSearchParams(), { replace: true });
			} else {
				localStorage.setItem("currentCampus", "C_" + plantel);
				dispatch(setCurrentUserCampusId(plantel));
				dispatch(setCurrentUserCampus("C_" + plantel));
			}
			return;
		}
		atendido.current = llave;
		if (nuevo) {
			if (allowed.create) setForm({ pedido: { nivelId: Number(params.get("nivel")) || null }, origen: "lista" });
			setParams(new URLSearchParams(), { replace: true });
			return;
		}
		const actualizar = !!params.get("actualizar");
		const id = campusRef[campusKey]?.id;
		http.horariosService.lista(id, { tab: "todos", q: codigo || "", perPage: 20 }).then((r) => {
			const fila = r.data.rows.find((x) => x.code === codigo);
			if (!fila) { setAviso("El horario del enlace no está en este plantel o ya no existe."); quitarEnlace(); return; }
			if (actualizar && matriculas.update) { setMatricula(fila); return; }
			setVentanaId(fila.id);
		}).catch((e) => { console.error(e); setAviso(S.errorLista); });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusRef, campusKey, params]);

	// ---- filtros
	const cambiarFiltros = (f: Filtros) => { setPage(1); setFiltros(f); };
	const limpiar = () => { setPage(1); setFiltros(FILTROS_VACIOS); setQ(""); };
	const cambiarTab = (t: TabKey) => { setPage(1); setTab(t); };

	// ---- formulario
	const abrirForm = (p: FormularioPedido, origen: Origen) => { setForm({ pedido: p, origen }); };
	const formGuardado = (id: number) => {
		const origen = form?.origen;
		setForm(null);
		refrescar();
		if (origen === "ocupacion") return;
		setVentanaId(id);
	};
	const formCerrado = () => {
		const f = form;
		setForm(null);
		if (f?.origen === "ventana" && f.pedido.horarioId) setVentanaId(f.pedido.horarioId);
	};

	// ---- borrar desde la fila
	const confirmarBorrar = async () => {
		if (!borrar || !campusId) return;
		setBorrando(true); setErrorBorrar(null);
		try {
			const r: any = await SchedulesService.removeSchedules(campusId, [borrar.id]);
			if (r.status === 200 || r.status === 204) { setBorrar(null); refrescar(); }
			else setErrorBorrar(S.borrarError);
		} catch (e: any) {
			console.error(e);
			setErrorBorrar(e?.response?.data?.message || S.borrarError);
		} finally { setBorrando(false); }
	};

	const menu = (f: FilaHorario) => [
		...(matriculas.update ? [{ texto: S.menu.actualizaciones, onClick: () => navigate("/enrollmentUpdates?horario=" + f.id) }] : []),
		...(allowed.delete && f.alumnas < 1 ? [{ texto: S.menu.borrar, peligro: true, onClick: () => { setErrorBorrar(null); setBorrar(f); } }] : [])
	];

	const total = datos?.total ?? 0;
	const last = Math.max(1, Math.ceil(total / perPage));
	const pages = useMemo(() => buildPages(page, last, setPage, movil), [page, last, movil]);
	const itemCount = { start: total ? (page - 1) * perPage + 1 : 0, end: Math.min(page * perPage, total), total };

	const permisos = { editar: !!allowed.update, borrar: !!allowed.delete, matricula: !!matriculas.update, lista: !!asistencias.read };
	const aulas = (opciones?.aulas || []).map((a) => ({ id: a.id, name: a.name }));

	return (
		<>
			<HorariosView
				movil={movil} tab={tab} counts={counts} onTab={cambiarTab}
				q={q} onBuscar={(v) => { setPage(1); setQ(v); }}
				filtros={filtros} onFiltros={cambiarFiltros} onLimpiar={limpiar} opciones={opciones}
				filas={datos ? datos.rows : null} loading={loading} error={error} aviso={aviso} onCerrarAviso={() => setAviso(null)}
				pages={pages} pageSizes={PAGE_SIZES} itemCount={itemCount} onPageSize={(n) => { setPage(1); setPerPage(n); }}
				puedeCrear={!!allowed.create} onNuevo={() => abrirForm({}, "lista")}
				onAbrir={(f) => abrirVentana(f.id, f.code)} menu={menu}
			/>

			{/* Ocupación de Aulas vive en Campus → Aulas → Ocupación (Esteban, 2026-11-02: «Sí, quítalo» de Horarios). */}
			{campusId && ventanaId !== null && !form ? (
				<VentanaHorario
					campusId={campusId} id={ventanaId} sym={sym} aulas={aulas} permisos={permisos}
					onClose={cerrarVentana}
					onEditar={(id) => { setVentanaId(null); abrirForm({ horarioId: id }, "ventana"); }}
					onActualizarMatricula={(f: FichaHorario) => { setVentanaId(null); setMatricula(f); }}
					onCambio={refrescar}
				/>
			) : null}

			{campusId && matricula ? (
				<ActualizarMatricula
					fila={matricula} campusId={campusId}
					onCerrar={() => { const id = matricula.id; setMatricula(null); quitarEnlace(); if (ventanaId === null) setVentanaId(id); }}
					onCambio={refrescar} onError={setAviso}
				/>
			) : null}

			<HorarioForm
				show={form !== null}
				horarioId={form?.pedido.horarioId ?? null} nivelId={form?.pedido.nivelId ?? null}
				aulaId={form?.pedido.aulaId ?? null} weekDay={form?.pedido.weekDay ?? null} inicio={form?.pedido.inicio ?? null}
				onGuardado={formGuardado} onCerrar={formCerrado}
			/>

			<Modal className="waModulo" show={borrar !== null} onHide={borrando ? undefined : () => setBorrar(null)} centered>
				<Modal.Header closeButton={!borrando}><Modal.Title style={{ fontSize: 18 }}>{S.borrar}</Modal.Title></Modal.Header>
				<Modal.Body style={{ textAlign: "left", fontSize: 15 }}>
					<div>{borrar ? S.confirmarBorrar(tituloHorario(borrar, cursoCorto) + " " + borrar.hora) : ""}</div>
					{errorBorrar ? <div className="hcAviso" style={{ marginTop: 10 }}>{errorBorrar}</div> : null}
				</Modal.Body>
				<Modal.Footer>
					<button type="button" className="btn btn-outline-secondary btn-sm" disabled={borrando} onClick={() => setBorrar(null)}>{S.cancelar}</button>
					<button type="button" className="btn btn-primary btn-sm" disabled={borrando} onClick={confirmarBorrar}>{S.borrar}</button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default HorariosController;
